import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { getAllProducts } from 'indices-list'
import { createPrices } from 'services/price-history'
import { assetConfig } from 'services/price-history/config'
import { ChainAsset, RawHistoricalIndexTimestamp } from 'services/price-history/interfaces'
import {
  getPriceRangesStore,
  getPricesStore,
  timestampToDayIndex,
  writePriceRangesStore,
  writePricesStore,
} from 'services/price-history/utils'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { assertMultiply } from 'utils'
import { noStakingProduct } from 'utils/product-service'
import { Address } from 'viem'
import { QUERY_KEY_ALL_DAILY_STATS } from '../../keys'
import { historicalAssetsStatsQuery } from '../index/daily-statistics'
import { getProductPortfolio } from '../index/daily-statistics/utils'
import { DailyCapital } from './model'

const useAllDailyStatsQuery = async <T = DailyCapital>({
  queryKey: [key],
}: {
  queryKey: [string]
}): Promise<T[]> => {
  const products = getAllProducts()

  const results = await Promise.all(
    products.filter(noStakingProduct).map(async (product) => {
      const portfolio = await historicalAssetsStatsQuery(
        key,
        product.network,
        product.address,
        'useAllDailyStatsQuery',
      )
      const { prices, isError } = await createPrices(
        assetConfig,
        portfolio,
        getPricesStore,
        getPriceRangesStore,
        writePricesStore,
        writePriceRangesStore,
      )
      const symbol = `${product.network}:${product.address}` as ChainAsset
      const productPortfolio = getProductPortfolio(symbol)
      const assetsPrices = getPricesStore()
      let intersection: RawHistoricalIndexTimestamp | undefined
      if (isError) {
        return [
          {
            id: product.address,
            isDailyCapital: true,
            basePrice: Big(0),
            basePriceETH: Big(0),
            totalSupply: Big(0),
            marketCap: Big(0),
            date: 0,
            isError: true,
          },
        ]
      }

      const parsedData: DailyCapital[] = prices[symbol].map((el) => {
        intersection =
          productPortfolio?.history.find((history) => el.timestamp === +history.timestamp) ??
          intersection
        const dayIndex = timestampToDayIndex(assetConfig, el.timestamp)
        let intermediateCap = Big(0)
        if (intersection !== undefined) {
          for (const asset of intersection.assets) {
            const assetKey = `${asset.chainID}:${asset.asset}` as ChainAsset
            const assetPrice = assetsPrices[assetKey]
            if (assetPrice !== undefined) {
              assertMultiply(asset.balance, assetPrice[dayIndex])
              intermediateCap = intermediateCap.add(Big(asset.balance).mul(assetPrice[dayIndex]))
            }
          }
        }
        return {
          id: product.address,
          isDailyCapital: true,
          basePrice: el.value,
          basePriceETH: Big(0),
          totalSupply: Big(intersection?.totalSupply ?? 0),
          marketCap: intermediateCap,
          date: el.timestamp,
          isError,
        }
      })

      return parsedData
    }),
  )
  const test = results.reduce<DailyCapital[]>((acc, curr) => {
    return [...acc, ...curr]
  }, [])
  return test as T[]
}
export interface ProductDetails {
  address: Address
  network: NetworkType
}

/**
 * Returns ALL daily statistics for all products
 */

export const userAllDailyStatisticsFactory = () => {
  const mainProperties = createAtomsWithQuery<DailyCapital[], null, null, null, [string]>(
    {},
    QUERY_KEY_ALL_DAILY_STATS,
    useAllDailyStatsQuery<DailyCapital>,
    DEFAULT_REFETCH_INTERVAL,
    'useAllDailyStatsQuery',
  )

  return mainProperties
}
